import React, { useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js/pure';
import type { StripeEmbeddedCheckout } from '@stripe/stripe-js';

import type { EmbeddedCheckoutSession } from '../../api/Plan';

interface CheckoutProps {
  session: EmbeddedCheckoutSession;
  onComplete: () => void;
  onDismiss: () => void;
}

const Checkout: React.FC<CheckoutProps> = ({ session, onComplete, onDismiss }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    let embeddedCheckout: StripeEmbeddedCheckout | undefined;
    setLoading(true);
    setError(null);

    const mountCheckout = async () => {
      try {
        const stripe = await loadStripe(session.publishable_key);
        if (!active) return;
        if (!stripe) throw new Error('Stripe could not be loaded. Please try again.');
        const checkout = await stripe.createEmbeddedCheckoutPage({
          clientSecret: session.client_secret,
          onComplete: () => {
            if (active) onComplete();
          },
        });
        embeddedCheckout = checkout;
        if (!active) {
          checkout.destroy();
          embeddedCheckout = undefined;
          return;
        }
        if (!containerRef.current) throw new Error('The checkout form could not be displayed.');
        checkout.mount(containerRef.current);
        setLoading(false);
      } catch {
        embeddedCheckout?.destroy();
        embeddedCheckout = undefined;
        if (active) {
          setLoading(false);
          setError('Unable to load the secure payment form. Please try again or contact support.');
        }
      }
    };
    void mountCheckout();

    return () => {
      active = false;
      embeddedCheckout?.destroy();
      embeddedCheckout = undefined;
    };
  }, [session, onComplete, attempt]);

  return (
    <section className="inner wa-stack wa-gap-s" aria-label="Complete your subscription">
      <wa-button appearance="outlined" onClick={onDismiss}>Back to plans</wa-button>
      {loading && <p role="status">Loading secure payment form...</p>}
      {error && (
        <wa-callout variant="danger" role="alert">
          {error}
          <wa-button appearance="plain" onClick={() => setAttempt((value) => value + 1)}>Try again</wa-button>
        </wa-callout>
      )}
      <div ref={containerRef} />
    </section>
  );
};

export default Checkout;
